import UserDriverEditForm, {
  UserDriverEditFormAddress,
  UserDriverEditFormFleetPartner,
} from "../../../../../../common/services/user/driver/edit-form/user-driver-edit-form";
import UserCitizenship from "../../../../../../common/types/user-citizenship";
import UserFormOfEmployment from "../../../../../../common/types/user-form-of-employment";
import UserLanguage from "../../../../../../common/types/user-language";
import UserVehicleOwnership from "../../../../../../common/types/user-vehicle-ownership-form";
import DriverEditAccountFormData from "../form/types/driver-edit-account-form-data";
import DriverEditCompanyFormData from "../form/types/driver-edit-company-form-data";
import DriverEditVehicleFormData from "../form/types/driver-edit-vehicle-form-data";
import TaxiDriverEditUserFormData from "../form/types/taxi-driver-edit-user-form-data";
import DriverEditAddress from "../types/driver-edit-address";
import DriverEditAddressType from "../types/driver-edit-address-type";
import DriverEditCarOwnershipType from "../types/driver-edit-car-ownership-type";
import DriverEditCitizenshipType from "../types/driver-edit-citizenship-type";
import DriverEditFormOfEmploymentType from "../types/driver-edit-form-of-employment-type";
import DriverEditLanguageType from "../types/driver-edit-language-type";
import DriverEditUserFleetPartnerSelectOption from "../types/driver-edit-user-fleet-partner-select-option";
import DriverEditUserPhoneNumber from "../types/driver-edit-user-phone-number";

const createFleetPartnerSelectOption = (
  fleetPartnerDataItem: UserDriverEditFormFleetPartner,
): DriverEditUserFleetPartnerSelectOption => {
  return {
    value: {
      name: fleetPartnerDataItem.name,
      uuid: fleetPartnerDataItem.uuid,
    },
    label: fleetPartnerDataItem.name,
  };
};

const createFleetPartnerSelectOptions = (
  responseFleetPartners: UserDriverEditFormFleetPartner[],
): DriverEditUserFleetPartnerSelectOption[] => {
  return responseFleetPartners.map(createFleetPartnerSelectOption);
};

const createCarOwnershipType = (
  type: UserVehicleOwnership,
): DriverEditCarOwnershipType | null => {
  switch (type) {
    case UserVehicleOwnership.BUSINESS:
      return DriverEditCarOwnershipType.BUSINESS;
    case UserVehicleOwnership.OWNER:
      return DriverEditCarOwnershipType.OWNER;
  }
};

const createCitizenship = (
  citizenship: UserCitizenship,
): DriverEditCitizenshipType | null => {
  switch (citizenship) {
    case UserCitizenship.BY:
      return DriverEditCitizenshipType.BY;
    case UserCitizenship.CZ:
      return DriverEditCitizenshipType.CZ;
    case UserCitizenship.DE:
      return DriverEditCitizenshipType.DE;
    case UserCitizenship.LT:
      return DriverEditCitizenshipType.LT;
    case UserCitizenship.PL:
      return DriverEditCitizenshipType.PL;
    case UserCitizenship.RU:
      return DriverEditCitizenshipType.RU;
    case UserCitizenship.SK:
      return DriverEditCitizenshipType.SK;
    case UserCitizenship.UA:
      return DriverEditCitizenshipType.UA;
  }
};

const createFormOfEmployment = (
  formOfEmployment: UserFormOfEmployment,
): DriverEditFormOfEmploymentType | null => {
  switch (formOfEmployment) {
    case UserFormOfEmployment.B2B:
      return DriverEditFormOfEmploymentType.B2B;
    case UserFormOfEmployment.EMPLOYMENT:
      return DriverEditFormOfEmploymentType.EMPLOYMENT;
  }
};

const createLanguageType = (type: UserLanguage): DriverEditLanguageType => {
  switch (type) {
    case UserLanguage.BY:
      return DriverEditLanguageType.BY;
    case UserLanguage.CZ:
      return DriverEditLanguageType.CZ;
    case UserLanguage.DE:
      return DriverEditLanguageType.DE;
    case UserLanguage.LT:
      return DriverEditLanguageType.LT;
    case UserLanguage.PL:
      return DriverEditLanguageType.PL;
    case UserLanguage.RU:
      return DriverEditLanguageType.RU;
    case UserLanguage.SK:
      return DriverEditLanguageType.SK;
    case UserLanguage.UA:
      return DriverEditLanguageType.UA;
    case UserLanguage.EN:
      return DriverEditLanguageType.EN;
    case UserLanguage.ES:
      return DriverEditLanguageType.ES;
    case UserLanguage.FR:
      return DriverEditLanguageType.FR;
  }
};

const createLanguagesType = (
  types: UserLanguage[],
): DriverEditLanguageType[] => {
  return types.map(createLanguageType);
};

const createAddressType = (
  addressTypeDataItem: string,
): DriverEditAddressType => {
  switch (addressTypeDataItem) {
    case "MAILING":
      return DriverEditAddressType.MAILING;
    case "STARTING":
      return DriverEditAddressType.STARTING;
    default:
      return DriverEditAddressType.STARTING;
  }
};

const createAddress = (
  address: UserDriverEditFormAddress,
): DriverEditAddress => {
  return {
    apartmentNumber: address.apartment,
    country: address.country,
    description: address.description,
    houseNumber: address.houseNo,
    latitude: address.lat,
    longitude: address.lon,
    street: address.street,
    town: address.town,
    type: createAddressType(address.type),
    zipCode: address.zipCode,
  };
};

const createAddresses = (
  addresses: UserDriverEditFormAddress[],
): DriverEditAddress[] => {
  return addresses.map(createAddress);
};

const createMobile = (
  dialingCode: string,
  number: string,
): DriverEditUserPhoneNumber => {
  return {
    dialingCode: dialingCode ?? undefined,
    number: number ?? undefined,
  };
};

const createVehicleFormData = (
  driverEditFormDataResponse: UserDriverEditForm["driver"],
): DriverEditVehicleFormData => {
  return {
    make: driverEditFormDataResponse.carMake,
    model: driverEditFormDataResponse.carModel,
    productionYear: driverEditFormDataResponse.carProdYear,
    numberOfSeats: driverEditFormDataResponse.carSeatsNo,
    registrationNumber: driverEditFormDataResponse.carRegNo,
    ownership: createCarOwnershipType(driverEditFormDataResponse.carOwnership),
  };
};

const createCompanyFormData = (
  driverEditFormDataResponse: UserDriverEditForm,
): DriverEditCompanyFormData => {
  const associationDataItem = driverEditFormDataResponse.associations[0];
  return {
    name: associationDataItem.companyName ?? "",
    companyId: associationDataItem.regon ?? "",
    taxNumber: associationDataItem.taxId ?? "",
    nationalCourtRegisterNumber: associationDataItem.krsNo ?? "",
    country: associationDataItem.country ?? "",
    zipCode: associationDataItem.zipCode ?? "",
    town: associationDataItem.town ?? "",
    street: associationDataItem.street ?? "",
    houseNumber: associationDataItem.houseNo ?? "",
    apartmentNumber: associationDataItem.apartment ?? "",
    description: associationDataItem.description ?? "",
  };
};

const createAccountFormData = (
  driverEditFormDataResponse: UserDriverEditForm,
): DriverEditAccountFormData => {
  const userDataItem = driverEditFormDataResponse.user;
  return {
    username: userDataItem.username,
    email: userDataItem.email,
  };
};

const createUserFormData = (
  driverEditFormData: UserDriverEditForm,
): TaxiDriverEditUserFormData => {
  const userFormData = driverEditFormData.user;
  const driverFormData = driverEditFormData.driver;
  const associationFormData = driverEditFormData.associations[0];
  const addressesFormData = driverEditFormData.addresses;

  return {
    addresses: createAddresses(addressesFormData),
    alternativeMobile: createMobile(
      userFormData.mobileSecondaryPrefix,
      userFormData.mobileSecondary,
    ),
    birthDate: new Date(driverFormData.birthDate),
    birthPlace: driverFormData.birthPlace,
    citizenship: createCitizenship(driverFormData.citizenship),
    firstName: userFormData.firstName,
    fleetPartner: associationFormData.fleetPartner,
    formOfEmployment: createFormOfEmployment(associationFormData.contractType),
    languages: createLanguagesType(driverFormData.languages),
    lastName: userFormData.lastName,
    mobile: createMobile(
      userFormData.mobilePrimaryPrefix,
      userFormData.mobilePrimary,
    ),
    personalIdNumber: driverFormData.idNumber,
    yearsOfExperience: driverFormData.experience,
  };
};

const taxiDriverEditFactory = {
  createUserFormData,
  createAccountFormData,
  createCompanyFormData,
  createVehicleFormData,
  createFleetPartnerSelectOptions,
};

export default taxiDriverEditFactory;
